import { FC, useState } from 'react';
import { Search } from 'lucide-react';
import { Source } from '../mock/types';
import { ChipPopover } from './ChipPopover';

type MetaAccountsChipProps = {
  sources: Source[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
};

const MetaLogo: FC<{ height?: number }> = ({ height = 12 }) => (
  <svg height={height} viewBox='0 0 64 36' style={{ width: 'auto' }} aria-hidden>
    <defs>
      <linearGradient id='meta-grad-a' x1='0%' y1='0%' x2='100%' y2='100%'>
        <stop offset='0%' stopColor='#0064E0' />
        <stop offset='100%' stopColor='#1877F2' />
      </linearGradient>
    </defs>
    <path
      fill='url(#meta-grad-a)'
      d='M9 6c5.5 0 9 4.8 12 11 3-6.2 6.5-11 12-11 6 0 9.5 5.5 9.5 13s-3.5 13-9.5 13c-3.6 0-6.3-2.7-8.7-6.7-1.2 1.8-2.4 3.5-3.7 4.9C18.2 32.5 14 30 11.7 25.8 9.4 21.6 9 17.5 9 13c0-4 1.5-7 5-7zm22.9 6.5c-3.5 4.5-5.8 9.4-7.5 14.3 1.7 3.4 3.6 5.6 6.4 5.6 4.5 0 6.7-3.9 6.7-9.4s-2.1-10.5-5.6-10.5z'
    />
  </svg>
);

export const MetaAccountsChip: FC<MetaAccountsChipProps> = ({ sources, selectedIds, onChange }) => {
  const [draft, setDraft] = useState<string[]>(selectedIds);
  const [query, setQuery] = useState('');

  const allMeta = sources.filter((s) => s.platform === 'meta');
  const filtered = allMeta.filter((s) => !query || s.label.toLowerCase().includes(query.toLowerCase()) || s.externalAccountId.includes(query));

  const toggle = (id: string) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <ChipPopover
      icon={<MetaLogo height={12} />}
      label='Meta'
      value='Polimo Ads'
      suffix={<span style={{ color: '#9CA3B1', fontWeight: 400, fontSize: 11.5 }}>· {selectedIds.length} ad account</span>}
      popWidth={420}
      onOpenChange={(open) => {
        if (open) setDraft(selectedIds);
      }}
    >
      {(close) => (
        <>
          <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid #F0F2F5', position: 'relative' }}>
            <Search size={14} color='#9CA3B1' style={{ position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Cerca ad account…'
              style={{
                width: '100%',
                border: '1px solid #DEE2E8',
                borderRadius: 8,
                padding: '8px 10px 8px 32px',
                fontSize: 13,
                outline: 'none',
                fontFamily: 'inherit',
                color: '#0B1733',
                background: '#F7F8FA',
              }}
            />
          </div>
          <div style={popSecStyle}>Business Manager · Polimo</div>
          <div style={{ maxHeight: 380, overflowY: 'auto', padding: '0 0 6px' }}>
            {filtered.map((s) => {
              const isSelected = draft.includes(s.externalAccountId);
              return (
                <div
                  key={s.externalAccountId}
                  onClick={() => toggle(s.externalAccountId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    cursor: 'pointer',
                    fontSize: 13,
                    background: isSelected ? '#E8EFFF' : 'transparent',
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      border: '1.5px solid ' + (isSelected ? '#1452E6' : '#C6CBD4'),
                      background: isSelected ? '#1452E6' : 'transparent',
                      color: 'white',
                      fontSize: 9,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '0 0 14px',
                    }}
                  >
                    {isSelected ? '✓' : ''}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: isSelected ? '#0F3FBD' : '#0B1733', fontWeight: isSelected ? 600 : 500 }}>{s.label}</span>
                      <span style={s.access === 'Owned' ? pillOwn : pillPar}>{s.access}</span>
                    </div>
                    <div style={cidStyle}>
                      {s.externalAccountId} ·{' '}
                      <span style={{ color: '#7A8597' }}>
                        {s.currency} · {s.timezone}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div
            style={{
              borderTop: '1px solid #F0F2F5',
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              fontSize: 12.5,
              color: '#58657B',
            }}
          >
            <span>
              {draft.length} di {allMeta.length} selezionati
            </span>
            <button
              type='button'
              onClick={() => {
                onChange(draft);
                close();
              }}
              style={btnPrimary}
            >
              Applica
            </button>
          </div>
        </>
      )}
    </ChipPopover>
  );
};

const popSecStyle: React.CSSProperties = {
  padding: '8px 14px 4px',
  fontSize: 10.5,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#9CA3B1',
  fontWeight: 600,
};

const pillOwn: React.CSSProperties = {
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  padding: '1px 6px',
  borderRadius: 4,
  fontWeight: 600,
  background: '#DDF3E7',
  color: '#16955E',
};

const pillPar: React.CSSProperties = {
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  padding: '1px 6px',
  borderRadius: 4,
  fontWeight: 600,
  background: '#FBEACE',
  color: '#B97206',
};

const cidStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
  fontSize: 11,
  color: '#9CA3B1',
};

const btnPrimary: React.CSSProperties = {
  background: '#1452E6',
  color: 'white',
  border: 0,
  fontFamily: 'inherit',
  padding: '6px 12px',
  borderRadius: 8,
  fontSize: 12.5,
  fontWeight: 600,
  cursor: 'pointer',
};
